import {
  CampaignDiscounts,
  Promotion,
  PromotionStatus,
  PROMOTION_STATUSES,
  PromotionRedeemError, PROMOTION_REDEEM_ERROR_MESSAGES, Discount
} from '@/types/promotions';
import { Schedule } from '@/types/schedules';
import { filter, flow, identity, map, sum, take } from 'lodash/fp';
import moment from 'moment';
import { AuthorizedUser } from '@/types/users';
import {
  CampaignFrequency,
  CampaignClustersPricesRecord,
  CampaignClustersDiscountsRecord
} from '@/types/campaigns';
import { FirebaseAppFirestore } from '@/firebase/firebase-app';
import firebaseNames from '@/statics/firebase-names';
import { Nullable } from '@/types/misc';
import { AdSpace, PublishedAdSpace } from '@/types/adspaces';
import { ScreenCluster } from '@/types/screen-cluster';
import { getLocationPriceByFrequency } from '@/utils/locations';

export const checkPromotionStatus = (item: Promotion): PromotionStatus => {
  const { START_DATE, END_DATE } = item;
  const toMoment = (date: any) => {
    if (date?.seconds) return moment(date.seconds * 1000);
    if (date?.toDate) return moment(date.toDate());
    return moment(date);
  };
  const startDateMoment = toMoment(START_DATE);
  const endDateMoment = toMoment(END_DATE);

  const nowMoment = moment();
  const promoExpired = nowMoment.isAfter(endDateMoment);
  const promoStarted = nowMoment.isAfter(startDateMoment);
  return promoExpired
    ? PROMOTION_STATUSES.EXPIRED
    : promoStarted
      ? PROMOTION_STATUSES.ACTIVE
      : PROMOTION_STATUSES.UPCOMING;
};

export function validateDiscountPromotion(promotion: Promotion, subTotal: number): CampaignDiscounts {
  const totalDiscount = promotion.VALUE_CAP && subTotal > promotion.VALUE_CAP
    ? Math.round(promotion.VALUE_CAP * (promotion.TYPE.VALUE / 100))
    : Math.round(subTotal * (promotion.TYPE.VALUE / 100));

  return {
    totalDiscount,
    discountList: [
      { discountReason: promotion.REDEEM_MESSAGE, discountValue: totalDiscount },
    ],
  };
}

export function validateFixedPricePromotion(promotion: Promotion, campaignApplicableSchedules: Schedule[]): CampaignDiscounts {
  const campaignSchedulesToApplyThePromotionOn = flow(
    filter((schedule: Schedule) => schedule.TOTAL_BILL_PRICE > promotion.TYPE.VALUE),
    promotion.WEEKS_PERIOD_CAP
      ? take(promotion.WEEKS_PERIOD_CAP)
      : identity,
  )(campaignApplicableSchedules);

  const totalDiscount = flow(
    map((schedule: Schedule) => schedule.TOTAL_BILL_PRICE - promotion.TYPE.VALUE),
    sum,
  )(campaignSchedulesToApplyThePromotionOn);

  return {
    totalDiscount,
    discountList: [
      { discountReason: promotion.REDEEM_MESSAGE, discountValue: totalDiscount },
    ],
  };
}

interface CampaignParams {
  startDate: Date;
  durationInWeeks: number;
  frequency: CampaignFrequency;
  adSpaces: PublishedAdSpace[];
}

async function getDefaultPromotions(): Promise<Promotion[] | null> {
  try {
    const promotionsSnap = await FirebaseAppFirestore
      .collection(firebaseNames.PROMOTIONS)
      .where('DEFAULT', '==', true)
      .get();

    if (promotionsSnap.empty) {
      return null;
    }
    return promotionsSnap.docs.map((doc) => doc.data() as Promotion);
  } catch (error) {
    console.error('Failed to fetch default promotions:', error);
    return null;
  }
}

export async function calculateDefaultPromoDiscount(
  userInfo: AuthorizedUser,
  campaignParams: CampaignParams
): Promise<Discount[]> {
  const defaultPromotions = await getDefaultPromotions();
  const emptyDiscount = [] as Discount[];

  if (!defaultPromotions) {
    return emptyDiscount;
  }
  const errorMessages = defaultPromotions.map((defaultPromotion) =>
    validateCampaignPromotionOffer(
      defaultPromotion,
      userInfo,
      campaignParams
    )
  )
  const allPromotionNotActive = errorMessages.every((error) => error)
  if (allPromotionNotActive) {
    return emptyDiscount;
  }
  const allValidPromotions = defaultPromotions.filter((defaultPromotion, index) => !errorMessages[index])
    .map((defaultPromotion) =>
      validatePercentageDiscountPromotion(
        defaultPromotion,
        campaignParams
      )
    )
  return combineAllValidPromotions(allValidPromotions, campaignParams.adSpaces) as Discount[];
}

function combineAllValidPromotions(campaignDiscounts: CampaignDiscounts[], adSpaces: PublishedAdSpace[]) {
  const discountsByClusterId: Record<ScreenCluster['ID'], Nullable<Discount>> = adSpaces.reduce((acc, adSpace) => ({
    ...acc,
    [adSpace.SCREEN_CLUSTER_REF.id]: null as Nullable<Discount>
  }), {})
  campaignDiscounts.forEach((campaignDiscount) => {
    Object.entries(campaignDiscount.screenClustersDiscountsRecord || {}).forEach(([ screenClusterId, value ]: [ ScreenCluster['ID'], Nullable<Discount> ]) => {
      if (!discountsByClusterId[screenClusterId]?.discountValue) {
        discountsByClusterId[screenClusterId] = { ...value, screenClusterId } as Discount;
      } else if (Number(value?.discountValue) > Number(discountsByClusterId[screenClusterId]?.discountValue)) {
        discountsByClusterId[screenClusterId] = { ...value, screenClusterId } as Discount;
      }

    })
  })
  return Object.values(discountsByClusterId).filter(Boolean).filter((discounts) => discounts?.discountValue)
}

function validateCampaignPromotionOffer(
  promotion: Promotion,
  userInfo: AuthorizedUser,
  campaignParams: CampaignParams
): PromotionRedeemError | null {
  const {
    adSpaces: campaignAdSpaces,
    durationInWeeks: campaignDurationInWeeks,
  } = campaignParams;

  const promotionStatus = checkPromotionStatus(promotion);
  const campaignApplicableLocations = filterByPromotionApplicableLocations(
    promotion,
    campaignAdSpaces
  );
  const isFirstCampaignOnlyPromo = () => promotion.FIRST_PUBLISHED_CAMPAIGN_ONLY;
  const userHasPublishedCampaigns = () => userInfo?.HAS_PUBLISHED_CAMPAIGNS;
  const hasLocationRestriction = () => !campaignApplicableLocations.length;
  const hasMinWeekRequirement = () => promotion.MINIMUM_NUMBER_OF_WEEKS_IN_CAMPAIGN;
  const meetsDurationRequirement = () => campaignDurationInWeeks >= (promotion.MINIMUM_NUMBER_OF_WEEKS_IN_CAMPAIGN || 0);
  const isAnonymousUser = () => !userInfo;

  const userCampaignError =
    isFirstCampaignOnlyPromo() &&
    (isAnonymousUser() || userHasPublishedCampaigns()) &&
    PROMOTION_REDEEM_ERROR_MESSAGES.userAlreadyHasPublishedCampaigns;

  const campaignEligibilityError =
    (hasLocationRestriction() && PROMOTION_REDEEM_ERROR_MESSAGES.campaignNotEligibleForPromotion) ||
    (hasMinWeekRequirement() && !meetsDurationRequirement() && PROMOTION_REDEEM_ERROR_MESSAGES.campaignNotEligibleForPromotion);

  const promotionStatusError: PromotionRedeemError =
    ({
      [PROMOTION_STATUSES.UPCOMING]: PROMOTION_REDEEM_ERROR_MESSAGES.promotionNotStarted,
      [PROMOTION_STATUSES.EXPIRED]: PROMOTION_REDEEM_ERROR_MESSAGES.promotionExpired,
    } as Record<PromotionStatus, PromotionRedeemError>)[promotionStatus];
  const errorMessage = userCampaignError
    || campaignEligibilityError
    || promotionStatusError;

  return errorMessage;
}

const filterByPromotionApplicableLocations = (
  { APPLICABLE_AD_SPACE_IDS = [] }: Promotion,
  campaignAdSpaces: PublishedAdSpace[]
): PublishedAdSpace[] => {
  const applicableAdSpaceIds = APPLICABLE_AD_SPACE_IDS.reduce((adSpaceIds, adSpaceId) =>
    ({ ...adSpaceIds, [adSpaceId]: adSpaceId }), {} as Record<PublishedAdSpace['ID'], PublishedAdSpace['ID']>)
  return campaignAdSpaces.filter(({ ID }) => applicableAdSpaceIds[ID]);
}
const calculateCampaignSubtotalPrices = (
  campaignAdSpaces: PublishedAdSpace[],
  campaignDurationInWeeks: number,
  campaignFrequencyOfAppearance: CampaignFrequency
): CampaignClustersPricesRecord => {
  return campaignAdSpaces
    .map((adSpace) => ({
      [adSpace.SCREEN_CLUSTER_REF.id]:
      getLocationPriceByFrequency(adSpace, campaignFrequencyOfAppearance) * (campaignDurationInWeeks || 0)
    }))
    .reduce((total, current) => ({ ...total, ...current }), {});
};

function validatePercentageDiscountPromotion(
  promotion: Promotion,
  campaignParams: CampaignParams
): CampaignDiscounts {
  const {
    adSpaces: campaignAdSpaces,
    durationInWeeks: campaignDurationInWeeks,
    frequency: campaignFrequencyOfAppearance,
  } = campaignParams;

  const campaignClustersPrices = calculateCampaignSubtotalPrices(
    campaignAdSpaces,
    campaignDurationInWeeks,
    campaignFrequencyOfAppearance
  );

  const calculateDiscountFromSubTotal = (
    clusterSubTotal: ScreenCluster['PRICE']
  ) =>
    ({
      discountReason: promotion.REDEEM_MESSAGE,
      discountType: promotion.TYPE,
      discountValue:
        promotion.VALUE_CAP && (clusterSubTotal || 0) > promotion.VALUE_CAP
          ? Math.round(promotion.VALUE_CAP * (promotion.TYPE.VALUE / 100))
          : Math.round((clusterSubTotal || 0) * (promotion.TYPE.VALUE / 100)),
    } as Discount);

  const filteredCampaignAdSpaces = filterByPromotionApplicableLocations(
    promotion,
    campaignAdSpaces
  );

  const screenClustersDiscountsRecord: CampaignClustersDiscountsRecord =
    filteredCampaignAdSpaces.reduce(
      (record, adSpace) => ({
        ...record,
        [adSpace.SCREEN_CLUSTER_REF.id]: calculateDiscountFromSubTotal(
          campaignClustersPrices[adSpace.SCREEN_CLUSTER_REF.id]
        ),
      }),
      {}
    );

  const totalDiscount = Object.values(screenClustersDiscountsRecord).reduce(
    (total, current) => total + (current ? current.discountValue : 0),
    0
  );

  const discountList = [
    {
      discountReason: promotion.REDEEM_MESSAGE,
      discountValue: totalDiscount,
      discountType: promotion.TYPE,
    },
  ];

  return {
    totalDiscount,
    discountList,
    screenClustersDiscountsRecord,
  };
}
